import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import MasterDashboard from "@/pages/MasterDashboard";
import MasterPlans from "@/pages/MasterPlans";
import MasterCompanies from "@/pages/MasterCompanies";
import CompanyKanban from "@/pages/CompanyKanban";
import CompanyChat from "@/pages/CompanyChat";
import CompanyConnections from "@/pages/CompanyConnections";
import CompanyAI from "@/pages/CompanyAI";
import CompanyTeam from "@/pages/CompanyTeam";
import CompanyTemplates from "@/pages/CompanyTemplates";
import CompanyBusinessHours from "@/pages/CompanyBusinessHours";
import MasterResellers from "@/pages/MasterResellers";
import MasterSiteSettings from "@/pages/MasterSiteSettings";
import ResellerDashboard from "@/pages/ResellerDashboard";
import ResellerCompanies from "@/pages/ResellerCompanies";
import ResellerBilling from "@/pages/ResellerBilling";
import ResellerLanding from "@/pages/ResellerLanding";
import Home from "@/pages/Home";
import Impersonate from "@/pages/Impersonate";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CompanyAntiBan from "@/pages/CompanyAntiBan";
import CompanyReports from "@/pages/CompanyReports";
import CompanyContacts from "@/pages/CompanyContacts";
import CompanyChatbot from "@/pages/CompanyChatbot";
import ChatbotDiagram from "@/pages/ChatbotDiagram";
import CompanyGeneralSettings from "@/pages/CompanyGeneralSettings";
import CompanyWorkSchedules from "@/pages/CompanyWorkSchedules";
import CompanyClosureReasons from "@/pages/CompanyClosureReasons";
import CompanyMassSending from "@/pages/CompanyMassSending";
import CompanyHelp from "@/pages/CompanyHelp";
import CompanyApiIntegration from "@/pages/CompanyApiIntegration";
import CompanySuggestions from "@/pages/CompanySuggestions";
import MasterSupport from "@/pages/MasterSupport";
import MasterSuggestions from "@/pages/MasterSuggestions";
import AppLayout from "@/components/app/AppLayout";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">Carregando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "master") return <Navigate to="/master" replace />;
  if (user.role === "reseller_owner") return <Navigate to="/reseller" replace />;
  return <Navigate to="/kanban" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Home />} />
          <Route path="/r/:slug" element={<ResellerLanding />} />
          <Route path="/impersonate" element={<Impersonate />} />

          <Route element={<Protected><AppLayout /></Protected>}>
            <Route path="/master" element={<Protected roles={["master"]}><MasterDashboard /></Protected>} />
            <Route path="/master/plans" element={<Protected roles={["master"]}><MasterPlans /></Protected>} />
            <Route path="/master/companies" element={<Protected roles={["master"]}><MasterCompanies /></Protected>} />
            <Route path="/master/resellers" element={<Protected roles={["master"]}><MasterResellers /></Protected>} />
            <Route path="/master/site-settings" element={<Protected roles={["master"]}><MasterSiteSettings /></Protected>} />
            <Route path="/reseller" element={<Protected roles={["reseller_owner"]}><ResellerDashboard /></Protected>} />
            <Route path="/reseller/companies" element={<Protected roles={["reseller_owner"]}><ResellerCompanies /></Protected>} />
            <Route path="/reseller/billing" element={<Protected roles={["reseller_owner"]}><ResellerBilling /></Protected>} />
            <Route path="/kanban" element={<Protected roles={["company_owner", "attendant", "master"]}><CompanyKanban /></Protected>} />
            <Route path="/chat" element={<Protected roles={["company_owner", "attendant", "master"]}><CompanyChat /></Protected>} />
            <Route path="/chat/:ticketId" element={<Protected roles={["company_owner", "attendant", "master"]}><CompanyChat /></Protected>} />
            <Route path="/connections" element={<Protected roles={["company_owner", "attendant", "master"]}><CompanyConnections /></Protected>} />
            <Route path="/ai" element={<Protected roles={["company_owner", "master"]}><CompanyAI /></Protected>} />
            <Route path="/team" element={<Protected roles={["company_owner", "master"]}><CompanyTeam /></Protected>} />
            <Route path="/templates" element={<Protected roles={["company_owner", "attendant", "master"]}><CompanyTemplates /></Protected>} />
            <Route path="/business-hours" element={<Protected roles={["company_owner", "master"]}><CompanyBusinessHours /></Protected>} />
            <Route path="/anti-ban" element={<Protected roles={["company_owner", "master"]}><CompanyAntiBan /></Protected>} />
            <Route path="/reports" element={<Protected roles={["company_owner", "master"]}><CompanyReports /></Protected>} />
            <Route path="/contacts" element={<Protected roles={["company_owner", "attendant", "master"]}><CompanyContacts /></Protected>} />
            <Route path="/chatbot" element={<Protected roles={["company_owner", "master"]}><CompanyChatbot /></Protected>} />
            <Route path="/chatbot/diagram" element={<Protected roles={["company_owner", "master"]}><ChatbotDiagram /></Protected>} />
            <Route path="/general-settings" element={<Protected roles={["company_owner", "master"]}><CompanyGeneralSettings /></Protected>} />
            <Route path="/work-schedules" element={<Protected roles={["company_owner", "master"]}><CompanyWorkSchedules /></Protected>} />
            <Route path="/closure-reasons" element={<Protected roles={["company_owner", "master"]}><CompanyClosureReasons /></Protected>} />
            <Route path="/mass-sending" element={<Protected roles={["company_owner", "master"]}><CompanyMassSending /></Protected>} />
            <Route path="/help" element={<Protected roles={["company_owner", "attendant", "master", "reseller_owner"]}><CompanyHelp /></Protected>} />
            <Route path="/integrations" element={<Protected roles={["company_owner", "master"]}><CompanyApiIntegration /></Protected>} />
            <Route path="/suggestions" element={<Protected roles={["company_owner", "attendant"]}><CompanySuggestions /></Protected>} />
            <Route path="/master/support" element={<Protected roles={["master"]}><MasterSupport /></Protected>} />
            <Route path="/master/suggestions" element={<Protected roles={["master"]}><MasterSuggestions /></Protected>} />
          </Route>
          {/* Catch-all fallback to prevent blank screens on unknown routes */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors offset="72px" />
    </AuthProvider>
  );
}

export default App;
